import { useEffect } from 'react';
import { ArrowRightIcon, ClockIcon, FlameIcon, MoveIcon, PhoneIcon, RulerIcon, SnowflakeIcon, SofaIcon, WindIcon } from 'lucide-react';
import { business, images, interiorHeroes, routes } from '../data/site';
const hero = interiorHeroes.fireTables;
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Fire Tables for Michigan Backyards | Pegasus Spas';
const DESCRIPTION = 'Compare fire tables for Michigan backyards, then visit Pegasus Spas in Brooklyn, Michigan to see materials, finishes and scale in person.';
const CANONICAL = 'https://www.pegasus-spas.com/60.html';
const FIRE_TABLE_IMAGE = 'https://drwzpk38qkpfb.cloudfront.net/www.pegasus-spas.com/uploaded/images/FireAndIce/cv30_glass_guard.jpg?opt=5';
const considerations = [{
  icon: RulerIcon,
  title: 'Size and Seating',
  points: ['The table should suit the space and the number of chairs around it.', 'Leave comfortable walking room on every side people use.', 'Round, square and rectangular shapes seat groups differently.', 'Height matters — some tables work as a coffee-height gathering point, others as a taller table.', 'Measure the spot you have in mind before choosing a size.']
}, {
  icon: FlameIcon,
  title: 'Everyday Use',
  points: ['Think about how often you expect to use it and in which seasons.', 'Clearances from furniture, structures and walkways matter.', 'Fuel requirements vary by product, so we will go over them with you.', 'Exposure to wind affects where a table works best.', 'Plan for seasonal protection when the table is not in use.']
}];
const planningItems = [{
  icon: MoveIcon,
  title: 'Placement & Footprint',
  copy: 'Measure the spot you have in mind, including the walking room around it, before choosing a size.'
}, {
  icon: SofaIcon,
  title: 'Furniture & Traffic Flow',
  copy: 'Think about where chairs sit, how people move past the table and what else shares the space.'
}, {
  icon: WindIcon,
  title: 'Fuel & Exposure',
  copy: 'Fire tables have their own fuel requirements, and open, windy spots behave differently. We will talk through the specifics with you.'
}, {
  icon: SnowflakeIcon,
  title: 'Seasonal Care',
  copy: 'Winter care varies by product. Follow the manufacturer guidance for the table you choose before freezing weather arrives.'
}];
const relatedLinks = [{
  label: 'Outdoor Furniture',
  href: routes.furniture
}, {
  label: 'Patio Umbrellas',
  href: routes.umbrellas
}, {
  label: 'Big Green Egg',
  href: routes.grills
}, {
  label: 'Current Specials',
  href: routes.specials
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function FireTablesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-1238" data-mp="16ulm54bgr84k">
      {/* Intro band */}
      <section aria-labelledby="fire-h1" className="relative w-full" data-id="element-1239" data-mp="16ttshz1ffkgx">
        <div className={`relative w-full overflow-hidden ${hero.frameClass}`} data-id="element-1240" data-mp="1bvj2rcgtru3y">
          <img src={hero.src} alt={hero.alt} className={hero.imageClass} width={hero.width} height={hero.height} decoding="async" data-id="element-1241" data-mp="1gu43re3wihtg" />
          <div className="hero-scrim-compact absolute inset-0 sm:hidden" aria-hidden="true" data-id="element-1242" data-mp="3ik5tz8avxxjm" />
          <div className="hero-scrim absolute inset-0 hidden sm:block" aria-hidden="true" data-id="element-1243" data-mp="1cvvoex2seflj" />

          <div className={`relative mx-auto flex max-w-container flex-col justify-center px-5 py-10 sm:px-8 ${hero.contentClass}`} data-id="element-1244" data-mp="17y3mrbccbf45">
            <p className="text-[14px] font-medium uppercase text-white sm:text-[15px]" data-id="element-1245" data-mp="1lv8ynryqj87b">
              Backyard Features
            </p>
            <h1 id="fire-h1" className="mt-3 max-w-3xl text-h1-mobile font-semibold text-white lg:text-[48px] lg:leading-[56px]" data-id="element-1246" data-mp="t8eekw5tyaud">
              Fire Tables for Michigan Backyards
            </h1>
            <p className="mt-4 max-w-2xl text-body text-white sm:text-body-lg" data-id="element-1247" data-mp="12z93y9edhqk1">
              A fire table gives a seating area a natural place to gather and keeps evenings
              comfortable later into the season. We help Michigan homeowners think through
              size, placement and seasonal care before choosing one.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-1248" data-mp="2h6gjc56ox29i">
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1249" data-mp="2h55fk5d8vuie">
                Visit the Showroom
              </SiteLink>
              <SiteLink to={routes.furniture} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white bg-white/10 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-1250" data-mp="30upd2jnvtgb4">
                Explore Backyard Living
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* What to think through */}
      <section aria-labelledby="compare-heading" className="w-full bg-white" data-id="element-1251" data-mp="1ffryv775dgrn">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1252" data-mp="1kgz8lbsuoq0y">
          <h2 id="compare-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1253" data-mp="10ebdq54n70xs">
            What to Think Through Before Choosing
          </h2>

          <div className="mt-8 grid grid-cols-1 gap-10 border-t border-ink/10 pt-8 lg:grid-cols-2 lg:gap-14" data-id="element-1254" data-mp="rkygws3gjf6f">
            {considerations.map((column, columnIndex) => {
            const Icon = column.icon;
            return <div key={column.title} className={columnIndex === 1 ? 'border-t border-ink/10 pt-8 lg:border-l lg:border-t-0 lg:pl-14 lg:pt-0' : ''} data-id="element-1255" data-mp="hovdliwvdahw" data-mpi={columnIndex}>
                  <h3 className="flex items-center gap-2.5 text-h3 font-semibold text-ink" data-id="element-1256" data-mp="3pdjqp4pv71o">
                    <Icon className="h-5 w-5 text-pine" aria-hidden="true" data-id="element-1257" data-mp="230en3ghgwoqr" />
                    {column.title}
                  </h3>
                  <ul className="mt-4 space-y-2" data-id="element-1258" data-mp="1xzzcuxshfxd0">
                    {column.points.map(point => <li key={point} className="text-[16.5px] leading-6 text-ink/80" data-id="element-1259" data-mp="2akft8nvb4r7o">
                        {point}
                      </li>)}
                  </ul>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Gathering place */}
      <section aria-labelledby="fire-tables-heading" className="w-full bg-water" data-id="element-1260" data-mp="1mmqmbmbzkpuq">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1261" data-mp="1mothslvwrb6u">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-1262" data-mp="1rltp104hujzj">
            <div className="lg:col-span-6" data-id="element-1263" data-mp="1wjd42290n0sj">
              <h2 id="fire-tables-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1264" data-mp="20lzy89t5sxsw">
                A Gathering Place with Warmth and Light
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1265" data-mp="3c03s7fzqylxy">
                A fire table changes how a seating area gets used. Scale is the first thing worth
                thinking through: the table should suit the space and the number of chairs around
                it, not crowd the walking room.
              </p>
              <p className="mt-3 text-body text-ink/80 sm:text-body-lg" data-id="element-1266" data-mp="bp76uxdmsrbc">
                From there, the useful showroom questions are practical ones — the surface it will
                sit on, clearances from furniture and structures, fuel requirements, and how exposed
                the spot is to wind.
              </p>
              <p className="mt-3 text-[16px] leading-6 text-ink/70" data-id="element-1267" data-mp="ulypr5p9h4ev">
                Ask us about current details and availability.
              </p>
            </div>

            <div className="lg:col-span-6" data-id="element-1268" data-mp="pl2mmucqhmai">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card border border-ink/10 bg-white" data-id="element-1269" data-mp="2wctlxyndr5m4">
                <img src={FIRE_TABLE_IMAGE} alt="Rectangular fire table with a clear glass wind guard around the burner" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-1270" data-mp="2rfjygojjx40x" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials and finishes */}
      <section aria-labelledby="materials-finishes-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-1271" data-mp="123wpwjljz35f">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1272" data-mp="s7bp6agkge7e">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-1273" data-mp="s58p0xlp94oy">
            <div className="order-2 lg:order-1 lg:col-span-6" data-id="element-1274" data-mp="e9wqef4fau2r">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card border border-ink/10 bg-white" data-id="element-1275" data-mp="j7olfi0d6wb6">
                <img src={images.pathBackyard} alt="Fire table surrounded by outdoor furniture and an umbrella on a lakeside patio" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-1276" data-mp="28mcvhclhg261" />
              </div>
            </div>

            <div className="order-1 lg:order-2 lg:col-span-6" data-id="element-1277" data-mp="1xgiby315dkyb">
              <h2 id="materials-finishes-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1278" data-mp="1xdw6c8xthfiq">
                Materials, Finishes and How It Fits the Patio
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1279" data-mp="2u4ygb58x1yvh">
                A fire table sits in the middle of a seating group, so its top surface, finish and
                proportions are part of the furniture layout rather than a separate decision. It
                helps to look at it next to the chairs and table you already have or plan to buy.
              </p>
              <p className="mt-3 text-body text-ink/80 sm:text-body-lg" data-id="element-1280" data-mp="19qel6w925foq">
                Options differ in surface material, finish and how the burner area is covered or
                guarded when it is not in use. Those differences are much easier to judge in person
                than from a photo.
              </p>
              <p className="mt-3 text-[16px] leading-6 text-ink/70" data-id="element-1281" data-mp="1b60j6ifc0x4k">
                Ask us which options and finishes are available now.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Planning checklist */}
      <section aria-labelledby="planning-heading" className="w-full bg-water" data-id="element-1282" data-mp="eh1a516xbrbb">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1283" data-mp="eeyadpfyyjte">
          <h2 id="planning-heading" className="max-w-3xl text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1284" data-mp="sa0lqs3l51dl">
            Plan for a Michigan Backyard
          </h2>

          <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1285" data-mp="3vvpx8t87oew7">
            {planningItems.map(item => {
            const Icon = item.icon;
            return <li key={item.title} data-id="element-1286" data-mp="1ut75gw1ebnok">
                  <Icon className="h-6 w-6 text-pine" aria-hidden="true" data-id="element-1287" data-mp="1ztuujc62dug5" />
                  <h3 className="mt-3 text-[19px] font-semibold leading-7 text-ink" data-id="element-1288" data-mp="1zs1mptjpt46k">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-[16px] leading-6 text-ink/75" data-id="element-1289" data-mp="29kh70xihi4um">{item.copy}</p>
                </li>;
          })}
          </ul>
        </div>
      </section>

      {/* Showroom split */}
      <section aria-labelledby="materials-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-1290" data-mp="1gwld20kaur1j">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1291" data-mp="1ltbpmdwwb4i7">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-1292" data-mp="1pwzweudpdw2e">
            <div className="lg:col-span-6" data-id="element-1293" data-mp="2x8qo5m4r64ff">
              <h2 id="materials-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1294" data-mp="2xc4m8i6dvrh2">
                See Materials and Scale in Person
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1295" data-mp="llwupvpihcs9">
                Size and finish are hard to judge from a photo. Stop by the Brooklyn showroom to see
                what a fire table actually looks like next to furniture, and to get current product
                information from us.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-1296" data-mp="6ybq1xczn5i8">
                <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-blue px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-1297" data-mp="1xxtpckr1k01">
                  <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-1298" data-mp="1sbevsuzn15ei" />
                  Location &amp; Hours
                </SiteLink>
                <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 rounded-btn border border-ink/25 px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1299" data-mp="1nd3i29682ar0">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1300" data-mp="azet03k2910g" />
                  Call or Text {business.phone}
                </a>
              </div>
            </div>

            <div className="lg:col-span-6" data-id="element-1301" data-mp="19vdn7vk3pac">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-1302" data-mp="192dwqv0m01p">
                <img src={images.backyardLiving} alt="Friends relaxing around a fire table on a backyard deck beside a hot tub" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-1303" data-mp="61vgp96xayb5" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navy CTA */}
      <section aria-labelledby="focal-heading" className="w-full bg-ink" data-id="element-1304" data-mp="3ji2uqc58c1bz">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1305" data-mp="2zuzr33chyycu">
          <div className="max-w-3xl" data-id="element-1306" data-mp="2uvwqpw7gpgb7">
            <h2 id="focal-heading" className="text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-1307" data-mp="2uwy8skmwb32f">
              Bring a Focal Point to the Backyard
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-1308" data-mp="34vnny5neq9ez">
              If you want warmth and light in the evening, we can help you plan where
              a fire table goes and how it works with the rest of the space.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-1309" data-mp="3sicbp7z3kw3i">
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1310" data-mp="3njivh7sfeqzn">
              Visit Our Brooklyn Showroom
            </SiteLink>
            <SiteLink to={routes.contact} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white/70 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-1311" data-mp="8hc1mvu3byr5">
              Contact Us
            </SiteLink>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-1312" data-mp="r5qrhfso96qd">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-1313" data-mp="w41yzjvhv55f">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1314" data-mp="113wuk0q7siwt">
            {relatedLinks.map(link => <li key={link.label} data-id="element-1315" data-mp="162rl5oufbbrh">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1316" data-mp="32vt5b84k5oqj">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1317" data-mp="1k78d3w7j4rz6" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}